import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Container,
    CssBaseline,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import { ThemeProvider } from "@mui/material/styles";
import { useThemeMode } from "../../theme/ThemeModeProvider";
import { createAppTheme } from "../../theme/appTheme";
import { fetchSchoolPage } from "../../redux/schoolPageRelated/schoolPageHandle";
import { getApiBaseUrl } from "../../config/api";
import { submitStudentApplication } from "../../utils/studentApplicationApi";
import { isOnSchoolSubdomain, publicSchoolPath } from "../../utils/schoolSlug";
import { MALAWI_DISTRICTS, GENDERS } from "../../components/student/studentEnrollmentConstants";

export default function StudentApplicationPage({ schoolSlugFromHost }) {
    const { slug: slugFromRoute } = useParams();
    const slug = schoolSlugFromHost || slugFromRoute;
    const dispatch = useDispatch();
    const { mode } = useThemeMode();
    const theme = useMemo(() => createAppTheme(mode), [mode]);
    const { schoolPage, loading, error } = useSelector((s) => s.schoolPage);

    const [classes, setClasses] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState("");
    const [formError, setFormError] = useState("");
    const [values, setValues] = useState({
        name: "",
        gender: "",
        dateOfBirth: "",
        guardianName: "",
        guardianPhone: "",
        guardianEmail: "",
        district: "",
        nationality: "Malawian",
        educationLevel: "Secondary",
        desiredClassId: "",
        previousSchool: "",
        notes: "",
    });

    useEffect(() => {
        if (slug) dispatch(fetchSchoolPage(slug));
    }, [dispatch, slug]);

    const schoolId = schoolPage?.schoolId || schoolPage?.school?._id || schoolPage?.school?.id;
    const schoolName = schoolPage?.schoolName || schoolPage?.school?.schoolName || "School";
    const schoolType = schoolPage?.schoolType || schoolPage?.school?.schoolType || "Secondary";

    useEffect(() => {
        if (!schoolId) return;
        let cancelled = false;
        axios
            .get(`${getApiBaseUrl()}/SclassList/${schoolId}`)
            .then(({ data }) => {
                if (!cancelled) setClasses(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!cancelled) setClasses([]);
            });
        return () => {
            cancelled = true;
        };
    }, [schoolId]);

    useEffect(() => {
        if (schoolType === "Primary") {
            setValues((v) => ({ ...v, educationLevel: "Primary" }));
        } else if (schoolType === "Secondary") {
            setValues((v) => ({ ...v, educationLevel: "Secondary" }));
        }
    }, [schoolType]);

    const classOptions = useMemo(() => {
        const list = Array.isArray(classes) ? classes : [];
        if (schoolType === "Primary") {
            return list.filter((c) => (c.educationLevel || "Secondary") === "Primary");
        }
        if (schoolType === "Secondary") {
            return list.filter((c) => (c.educationLevel || "Secondary") !== "Primary");
        }
        return list;
    }, [classes, schoolType]);

    const setField = (key, val) => setValues((v) => ({ ...v, [key]: val }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!schoolId) return;
        setSubmitting(true);
        setFormError("");
        setSuccess("");
        try {
            const data = await submitStudentApplication({
                schoolId,
                publicSlug: slug,
                ...values,
                desiredClassId: values.desiredClassId || undefined,
            });
            setSuccess(data?.message || "Application submitted successfully.");
            setValues({
                name: "",
                gender: "",
                dateOfBirth: "",
                guardianName: "",
                guardianPhone: "",
                guardianEmail: "",
                district: "",
                nationality: "Malawian",
                educationLevel: schoolType === "Primary" ? "Primary" : "Secondary",
                desiredClassId: "",
                previousSchool: "",
                notes: "",
            });
        } catch (err) {
            setFormError(err.response?.data?.message || "Could not submit application.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: { xs: 3, md: 5 } }}>
                <Container maxWidth="md">
                    <Button
                        component={RouterLink}
                        to={isOnSchoolSubdomain() ? "/" : publicSchoolPath(slug)}
                        startIcon={<ArrowBackIcon />}
                        sx={{ mb: 2 }}
                    >
                        Back to school page
                    </Button>

                    <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
                        <Typography variant="overline" color="primary.main" fontWeight={800}>
                            Admission application
                        </Typography>
                        <Typography variant="h4" fontWeight={800} gutterBottom>
                            Apply to {schoolName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Complete this form to apply for admission. The school office will review your application
                            and contact you. If accepted, your child will be registered and assigned to a class.
                        </Typography>

                        {loading ? (
                            <Typography color="text.secondary">Loading school details…</Typography>
                        ) : null}
                        {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}
                        {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}
                        {formError ? <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert> : null}

                        <Box component="form" onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Applicant full name"
                                        value={values.name}
                                        onChange={(e) => setField("name", e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Gender</InputLabel>
                                        <Select
                                            label="Gender"
                                            value={values.gender}
                                            onChange={(e) => setField("gender", e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Prefer not to say</em>
                                            </MenuItem>
                                            {GENDERS.map((g) => (
                                                <MenuItem key={g} value={g}>
                                                    {g}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="Date of birth"
                                        InputLabelProps={{ shrink: true }}
                                        value={values.dateOfBirth}
                                        onChange={(e) => setField("dateOfBirth", e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Guardian / parent name"
                                        value={values.guardianName}
                                        onChange={(e) => setField("guardianName", e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Guardian phone"
                                        value={values.guardianPhone}
                                        onChange={(e) => setField("guardianPhone", e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="email"
                                        label="Guardian email"
                                        value={values.guardianEmail}
                                        onChange={(e) => setField("guardianEmail", e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Home district</InputLabel>
                                        <Select
                                            label="Home district"
                                            value={values.district}
                                            onChange={(e) => setField("district", e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Select district</em>
                                            </MenuItem>
                                            {MALAWI_DISTRICTS.map((d) => (
                                                <MenuItem key={d} value={d}>
                                                    {d}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {schoolType === "Combined" ? (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Level applying for</InputLabel>
                                            <Select
                                                label="Level applying for"
                                                value={values.educationLevel}
                                                onChange={(e) => setField("educationLevel", e.target.value)}
                                            >
                                                <MenuItem value="Primary">Primary</MenuItem>
                                                <MenuItem value="Secondary">Secondary</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ) : null}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Preferred class (optional)</InputLabel>
                                        <Select
                                            label="Preferred class (optional)"
                                            value={values.desiredClassId}
                                            onChange={(e) => setField("desiredClassId", e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>No preference — office will assign</em>
                                            </MenuItem>
                                            {classOptions.map((c) => {
                                                const id = c._id || c.id;
                                                return (
                                                    <MenuItem key={id} value={id}>
                                                        {c.sclassName}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Previous school"
                                        value={values.previousSchool}
                                        onChange={(e) => setField("previousSchool", e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        label="Additional notes"
                                        placeholder="Medical needs, transfer reason, or other information for the office"
                                        value={values.notes}
                                        onChange={(e) => setField("notes", e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    startIcon={<SendIcon />}
                                    disabled={submitting || !schoolId}
                                >
                                    {submitting ? "Submitting…" : "Submit application"}
                                </Button>
                            </Stack>
                        </Box>
                    </Paper>
                </Container>
            </Box>
        </ThemeProvider>
    );
}
